import { ReactComponent as GarryClose } from 'assets/garry-close.svg'
import { ReactComponent as GarryIcon } from 'assets/garry-icon.svg'
import { ReactComponent as MobileHeaderClose } from 'assets/mobile-header-close.svg'
import { ReactComponent as WindscribeLogo } from 'assets/mobile-header-logo.svg'
import SplashScreen from 'components//SplashScreen'
import SubmitTicketView from 'components/SubmitTicketView'
import TicketSent from 'components/TicketSent'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { endChat } from 'redux/slices/message'
import { Page, pageActions } from 'redux/slices/page'
import { Box, Flex, IconButton } from 'theme-ui'
import GarryChatScreen from './GarryChatScreen'

// this syntax is required to get style-loader and css-loader working to bundle the CSS
// with the main JS file
// see https://github.com/storybookjs/presets/issues/191#issuecomment-812094476 for details
import '!style-loader!css-loader!global.css'

import { ReactComponent as CloseIcon } from 'assets/close-icon.svg'
import DescribeYourIssue from 'components/DescribeYourIssue'
import { helpActions } from 'redux/slices/help'
import { fadeIn } from 'utils/animations'
import useBreakpointIndex from 'theme/useBreakpointIndex'

const toggleButtonSize = '68px'
const toggleButtonMargin = '24px'
const mobileHeaderHeight = '72px'
const zIndex = 99999

export default function MainLayout(): ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const { page } = useAppSelector(state => state.page)
  const { hasDisplayedHelpMessage, hasClickedCloseHelpMessage } = useAppSelector(
    state => state.help,
  )
  const { chatEndsAt } = useAppSelector(x => x.message)
  const [isTabVisible, setIsTabVisible] = useState(true)
  const dispatch = useAppDispatch()
  const breakpointIndex = useBreakpointIndex()
  const isMobile = breakpointIndex === 0

  const shouldDisplayHelpMessage = hasDisplayedHelpMessage && !hasClickedCloseHelpMessage
  function onMouseEnter() {
    if (hasDisplayedHelpMessage) {
      return
    }

    setTimeout(() => {
      dispatch(helpActions.displayHelpMessage())
    }, 5000)
  }

  function checkIfChatHasEnded() {
    const timerInterval = setInterval(() => {
      exitConvoIfExpired()
    }, 1000)
    return timerInterval
  }

  function exitConvoIfExpired() {
    const currentTime = new Date()
    if (chatEndsAt && currentTime > chatEndsAt) {
      dispatch(endChat())
      dispatch(pageActions.switchPage(Page.SPLASH_PAGE))
    }
  }

  useEffect(() => {
    exitConvoIfExpired()
    const timerInterval = checkIfChatHasEnded()

    return () => {
      clearInterval(timerInterval)
    }
  }, [isTabVisible, chatEndsAt])

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const shouldOpenGarry = urlParams.get('garry')

    if (shouldOpenGarry === '1') {
      dispatch(pageActions.switchPage(Page.TRIAGE_VIEW))
      setIsOpen(true)
    }
  }, [])

  useEffect(() => {
    const checkIfTabIsVisible = () => {
      setIsTabVisible(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', checkIfTabIsVisible)

    return () => {
      document.removeEventListener('visibilitychange', checkIfTabIsVisible)
    }
  }, [])

  useEffect(() => {
    //gets the body
    const body = document.querySelector('body')
    //the goal here is to disable the scrolling behind the chat when its open,
    //to prevent the mobile's physical browser from scrolling behind it.
    if (body) {
      isOpen && isMobile ? body.classList.add('hide-scroll') : body.classList.remove('hide-scroll')
    }
  }, [isOpen, isMobile])

  return (
    <>
      {isOpen ? (
        <IconButton
          sx={{
            margin: 0,
            padding: 0,
            width: toggleButtonSize,
            height: toggleButtonSize,
            borderRadius: '50%',
            position: 'fixed',
            bottom: toggleButtonMargin,
            right: toggleButtonMargin,
            cursor: 'pointer',
            backgroundColor: 'neonGreen',
            zIndex,
            boxShadow: ({ colors }) => `0 10px 30px ${colors?.black30}`,
          }}
          onClick={() => setIsOpen(isOpen => !isOpen)}
          id="garry-toggle"
          data-testid="garry-toggle"
        >
          <GarryClose />
        </IconButton>
      ) : (
        <>
          <IconButton
            sx={{
              width: 'unset',
              height: 'unset',
              position: 'fixed',
              bottom: `calc(${toggleButtonMargin} - 4px)`,
              right: `calc(${toggleButtonMargin} - 12px)`,
              cursor: 'pointer',
              zIndex,
            }}
            onClick={() => setIsOpen(isOpen => !isOpen)}
            onMouseEnter={onMouseEnter}
            id="garry-toggle"
            data-testid="garry-toggle"
          >
            <GarryIcon
              sx={{
                filter: ({ colors }) => `drop-shadow(0 10px 30px ${colors?.black30})`,
                width: '84px',
                height: '84px',
              }}
            />
          </IconButton>
          {shouldDisplayHelpMessage && (
            <Box
              id="garry-help-tooltp"
              sx={{
                transition: 'opacity  0.5s',
                background: ({ colors }) => colors?.gray,
                boxShadow: ({ colors }) => `0 10px 30px ${colors?.black30}`,
                borderRadius: '20px',
                position: 'fixed',
                maxWidth: '135px',
                right: '10px',
                bottom: '105px',
                animation: `${fadeIn} 0.3s forwards ease-out`,
                mb: '20px',
                padding: '8px 16px',
                zIndex,
              }}
            >
              <Flex sx={{ alignItems: 'center', background: 'transparent' }}>
                <span
                  sx={{
                    fontSize: '16px',
                    fontWeight: '400',
                    fontFamily: 'Lato, sans-serif',
                    background: 'transparent',
                  }}
                  data-testid="need-help"
                >
                  Need help?
                </span>
                <IconButton
                  sx={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={e => {
                    e.stopPropagation()
                    dispatch(helpActions.closeHelpMessage())
                  }}
                  data-testid="close-need-help"
                >
                  <CloseIcon sx={{ background: 'transparent' }} />
                </IconButton>
              </Flex>
              <Box
                sx={{
                  borderTop: ({ colors }) => `15px solid ${colors?.gray}`,
                  background: 'transparent',
                  height: '10px',
                  width: 0,
                  borderLeft: 'none',
                  borderRight: '21px solid transparent',
                  pointerEvents: 'none',
                  position: 'absolute',
                  bottom: '-10px',
                  right: '12px',
                }}
              />
            </Box>
          )}
        </>
      )}
      <Flex
        id="garry-window"
        data-testid="garry-window"
        sx={{
          margin: 0,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          flexDirection: 'column',
          border: ({ colors }) => ['none', `2px solid ${colors?.white15}`],
          borderRadius: [0, '24px'],
          opacity: isOpen ? 1 : 0,
          transitionProperty: 'opacity',
          transitionDuration: '125ms',
          pointerEvents: isOpen ? 'auto' : 'none',
          position: 'fixed',
          height: ['100%', '600px'],
          maxHeight: '100%',
          width: ['100%', '360px'],
          bottom: [0, `calc(2 * ${toggleButtonMargin} + ${toggleButtonSize})`],
          right: [0, toggleButtonMargin],
          backgroundColor: 'garryBackground',
          justifyContent: 'center',
          zIndex,
        }}
      >
        <Box
          sx={{
            height: mobileHeaderHeight,
            width: '100%',
            display: ['flex', 'none'],
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '16px',
            flexShrink: 0,
          }}
        >
          <WindscribeLogo />
          <IconButton onClick={() => setIsOpen(false)}>
            <MobileHeaderClose />
          </IconButton>
        </Box>
        <Flex
          sx={{
            flex: 1,
            alignItems: 'center',
            // todo: make centring relative to whole window height ignoring mobile top bar,
            // but not overlapping with top bar
            // marginTop: [`-${mobileHeaderHeight}`, 0],
            // paddingTop: [mobileHeaderHeight, 0],
          }}
        >
          {page === Page.SPLASH_PAGE && <SplashScreen />}
          {page === Page.TRIAGE_VIEW && <DescribeYourIssue />}
          {page === Page.CHAT_PAGE && <GarryChatScreen />}
          {page === Page.SUBMIT_TICKET && <SubmitTicketView />}
          {page === Page.TICKET_SENT && <TicketSent setIsGarryOpen={setIsOpen} />}
        </Flex>
      </Flex>
    </>
  )
}
